<template>
  <div>
    <base-dialog :active="showWarning" :title="t('sharedialog.Shareable link')" :cancel-text="t('common.Cancel')"
      :confirm-text="t('common.Continue')" @close="enter_Inactive" @cancel="enter_Inactive"
      @confirm="enter_UserIsSettingOptions">
      <template #title>
        <div class="flex items-center">
          <i class="fa fa-share-alt mr-2"></i>{{ t('sharedialog.Shareable link') }}
        </div>
      </template>

      <div class="p-4 space-y-4">
        <p>
          {{ t('sharedialog.If you continue') }}
          {{ t('sharedialog.This link will work for anyone') }}
        </p>
        <p>
          {{ t('sharedialog.We want this website to be safe place for everyone') }}
        </p>
        <p>
          <router-link to="/terms-of-service" class="text-primary-500 hover:text-primary-600">
            {{ t('appInfo.Terms of service') }}
          </router-link>
        </p>
        <button
          class="btn bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800 mt-2"
          @click="enter_LoadingSharedWheels()">
          {{ t('sharedialog.See my shared wheels') }}
        </button>
      </div>
    </base-dialog>

    <base-dialog :active="showSharedWheels" :title="t('sharedialog.My shared wheels')" :cancel-text="t('common.Close')"
      @close="enter_Inactive" @cancel="enter_Inactive">
      <template #title>
        <div class="flex items-center">
          <i class="fa fa-share-alt mr-2"></i>{{ t('sharedialog.My shared wheels') }}
        </div>
      </template>

      <div class="p-4">
        <div class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead v-if="!noSharedWheels">
              <tr class="bg-stone-100 dark:bg-stone-700">
                <th class="py-2 px-4 text-left text-stone-800 dark:text-stone-200">{{ t('common.Wheel title') }}</th>
                <th class="py-2 px-4 text-left text-stone-800 dark:text-stone-200">{{ t('sharedialog.Link') }}</th>
                <th class="py-2 px-4 text-left text-stone-800 dark:text-stone-200"></th>
                <th v-if="isDesktop" class="py-2 px-4 text-left text-stone-800 dark:text-stone-200">{{
                  t('sharedialog.Created') }}</th>
                <th class="py-2 px-4 text-left text-stone-800 dark:text-stone-200">{{ t('sharedialog.Visits') }}</th>
                <th class="py-2 px-4 text-left text-stone-800 dark:text-stone-200"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="wheel in sharedWheels" :key="wheel.path"
                class="border-b border-stone-200 dark:border-stone-700">
                <td class="py-2 px-4 text-stone-800 dark:text-stone-200">{{ (wheel.config && wheel.config.title) || '-'
                }}
                </td>
                <td class="py-2 px-4 text-stone-800 dark:text-stone-200 whitespace-nowrap">
                  <a :href="getWheelLink(wheel.path)" class="text-primary-500 hover:text-primary-600">
                    {{ wheel.path }}
                  </a>
                </td>
                <td class="py-2 px-4">
                  <button class="btn btn-secondary btn-sm" @click="copyToClipboard(getWheelLink(wheel.path))">
                    <i class="far fa-clipboard"></i>
                  </button>
                </td>
                <td v-if="isDesktop" class="py-2 px-4 text-stone-800 dark:text-stone-200 whitespace-nowrap">
                  {{ formatTimeAgo(wheel.created) }}
                </td>
                <td class="py-2 px-4 text-right text-stone-800 dark:text-stone-200">{{
                  formatLocaleString(wheel.readCount ?? 0)
                }}</td>
                <td class="py-2 px-4">
                  <button class="btn btn-danger btn-sm" @click="enter_confirmingDelete(wheel.path)">
                    <i class="far fa-trash-alt"></i>
                    <span v-if="isDesktop" class="ml-1">{{ t('common.Delete') }}</span>
                  </button>
                </td>
              </tr>
              <tr v-if="noSharedWheels">
                <td colspan="6" class="py-4 px-4 text-stone-600 dark:text-stone-400 text-center">
                  {{ t('sharedialog.No wheels found') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </base-dialog>

    <base-dialog :active="showOptions" :title="t('sharedialog.Shareable link')" :cancel-text="t('common.Cancel')"
      :confirm-text="t('common.Continue')" @close="enter_Inactive" @cancel="enter_Inactive"
      @confirm="enter_CreatingLink">
      <template #title>
        <div class="flex items-center">
          <i class="fa fa-share-alt mr-2"></i>{{ t('sharedialog.Shareable link') }}
        </div>
      </template>

      <div class="p-4 space-y-4">
        <div>
          <label class="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
            {{ t('common.Wheel title') }}
          </label>
          <input type="text" v-model="wheelTitle" ref="wheelTitleField" maxlength="50"
            @keyup.enter="enter_CreatingLink()" class="form-input w-full">
        </div>

        <div class="my-4">
          <label class="flex items-center space-x-2 cursor-pointer">
            <input type="checkbox" v-model="showTitle"
              class="h-4 w-4 text-primary-500 focus:ring-primary-500 border-stone-300 rounded">
            <span class="text-stone-700 dark:text-stone-300">{{ t('sharedialog.Show title to people') }}</span>
          </label>
        </div>

        <div class="my-4">
          <p class="mb-2 text-stone-700 dark:text-stone-300">{{ t('sharedialog.What should a person be able to do') }}
          </p>

          <div class="space-y-2">
            <label class="flex items-start mt-2 space-x-2 cursor-pointer">
              <input type="radio" v-model="copyableWheel" :value="true"
                class="mt-1 h-4 w-4 text-primary-500 focus:ring-primary-500 border-stone-300">
              <span class="text-stone-700 dark:text-stone-300">{{
                t('sharedialog.They should be able to spin the wheel and')
              }}</span>
            </label>

            <label class="flex items-start mt-2 space-x-2 cursor-pointer">
              <input type="radio" v-model="copyableWheel" :value="false"
                class="mt-1 h-4 w-4 text-primary-500 focus:ring-primary-500 border-stone-300">
              <span class="text-stone-700 dark:text-stone-300">{{
                t('sharedialog.They should only be able to spin the wheel')
              }}</span>
            </label>
          </div>
        </div>
      </div>
    </base-dialog>

    <base-dialog :active="showLink" :title="t('sharedialog.Shareable link')" :cancel-text="t('common.Close')"
      @close="enter_Inactive" @cancel="enter_Inactive">
      <template #title>
        <div class="flex items-center">
          <i class="fa fa-share-alt mr-2"></i>{{ t('sharedialog.Shareable link') }}
        </div>
      </template>

      <div class="p-4 space-y-4">
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          {{ t('sharedialog.Link to this wheel') }}
        </p>

        <div class="flex flex-col md:flex-row gap-2 items-center">
          <div class="w-full md:w-2/3">
            <input id="sharableLinkText" type="text" v-model="sharableLink" class="form-input w-full">
          </div>
          <div class="w-full md:w-1/3">
            <button class="btn btn-secondary w-full" @click="copyLink">
              {{ t('sharedialog.Copy link') }}
            </button>
          </div>
        </div>

        <p class="mt-4 text-stone-600 dark:text-stone-400">
          {{ t('sharedialog.This link will work for anyone') }}
        </p>
      </div>
    </base-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWheelStore } from '@/stores/useWheelStore';
import { useUserStore } from '@/stores/useUserStore';
import { timeago, firestoremilliseconds, localestring } from '@utils/Filters.js';
import * as Locales from '@utils/Locales.js';
import * as Util from '@utils/Util.js';
import BaseDialog from '../shared/BaseDialog.vue';
import { useAccountPolicy } from '@/composables/useAccountPolicy.js';
import { onOpen as onDialogOpen } from '@/services/ui/DialogCoordinator.js';

const { t, locale } = useI18n();
const wheelStore = useWheelStore();
const userStore = useUserStore();
const instance = getCurrentInstance();
const { isLoggedIn, canCreateAnotherSharedWheel, ensurePolicyFresh } = useAccountPolicy();

const emit = defineEmits(['start-wait-animation', 'stop-wait-animation', 'show-snackbar-message', 'open-account-dialog']);

const fsm = ref('Inactive');
const wheelTitle = ref('');
const copyableWheel = ref(true);
const sharableLink = ref('');
const windowWidth = ref(window.innerWidth);
const mobileBreakpoint = 768;

function getManagementTokensFromStorage() {
  try {
    const tokens = localStorage.getItem('wheelManagementTokens');
    return tokens ? JSON.parse(tokens) : [];
  } catch (error) {
    console.error('Error reading management tokens from localStorage:', error);
    return [];
  }
}

const wheelTitleField = ref(null);
let unsubscribeDialogOpen = null;

// Close this dialog when another dialog opens
function closeSelf() {
  try {
    // Stop any active loader (safe even if none is active)
    emit('stop-wait-animation');
  } catch { }
  enter_Inactive();
}

onMounted(() => {
  window.addEventListener('resize', handleResize);
  window.addEventListener('storage', onStorage);
  // Subscribe to other dialog open events; close self if another dialog opens
  unsubscribeDialogOpen = onDialogOpen((name) => {
    if (name !== 'share') {
      closeSelf();
    }
  });
  handleResize();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('storage', onStorage);
  if (typeof unsubscribeDialogOpen === 'function') {
    try { unsubscribeDialogOpen(); } catch { }
  }
});

const isDesktop = computed(() => windowWidth.value >= mobileBreakpoint);
const sharedWheels = computed(() => userStore.sharedWheels);
const wheelConfig = computed(() => wheelStore.wheelConfig);
const noSharedWheels = computed(() => {
  const list = sharedWheels.value;
  return Array.isArray(list) ? list.length === 0 : true;
});

const showTitle = computed({
  get: () => wheelStore.wheelConfig.showTitle,
  set: (val) => wheelStore.setShowTitle(val)
});

const showWarning = computed({
  get: () => fsm.value === 'ShowingWarning',
  set: (val) => { if (val === false) fsm.value = 'Inactive'; }
});

const showSharedWheels = computed({
  get: () => ['ShowingSharedWheels', 'ConfirmingDelete', 'DeletingWheel'].includes(fsm.value),
  set: (val) => { if (val === false) fsm.value = 'Inactive'; }
});

const showOptions = computed({
  get: () => fsm.value === 'UserIsSettingOptions',
  set: (val) => { if (val === false) fsm.value = 'Inactive'; }
});

const showLink = computed({
  get: () => fsm.value === 'ShowingLink',
  set: (val) => { if (val === false) fsm.value = 'Inactive'; }
});

// Reload shared wheels when the list dialog becomes visible
watch(fsm, (val) => {
  if (val === 'ShowingSharedWheels') {
    userStore.loadSharedWheels();
  }
});

// If user logs in while this dialog is open, refresh policy immediately
watch(isLoggedIn, (now) => {
  if (now) {
    ensurePolicyFresh().catch((e) =>
      console.warn('Error refreshing policy after login:', e)
    );
  }
});

const show = () => {
  const cfg = wheelConfig.value;
  if (cfg.isTooBigForDatabase()) {
    alert(t('sharedialog.Sorry, too many images'));
    return;
  }
  wheelTitle.value = wheelStore.wheelTitle;
  enter_ShowingWarning();
};

const enter_ShowingWarning = () => {
  setState('ShowingWarning');
};

const enter_UserIsSettingOptions = async () => {
  // If the wheel is already shared, skip creation and show the existing link
  if (wheelStore.wheelIsShared && wheelStore.path) {
    const relativePath = Locales.getRelativeUrl(window.location.host, locale.value) + wheelStore.path;
    sharableLink.value = window.location.origin + relativePath;
    enter_ShowingLink();
    return;
  }

  setTimeout(() => { wheelTitleField.value && wheelTitleField.value.focus && wheelTitleField.value.focus(); }, 100);
  setState('UserIsSettingOptions');
};

const enter_LoadingSharedWheels = async () => {
  Util.trackEvent('Wheel', 'ViewSharedWheelsAttempt', '');
  setState('LoadingSharedWheels');
  emit('start-wait-animation');
  try {
    await userStore.loadSharedWheels();
    setState('ShowingSharedWheels');
    emit('stop-wait-animation');
    Util.trackEvent('Wheel', 'ViewSharedWheelsSuccess', '');
  } catch (error) {
    console.error('Error loading shared wheels:', error);
    emit('stop-wait-animation');
    alert('Failed to load shared wheels. Please try again.');
    enter_Inactive();
  }
};

const enter_CreatingLink = async () => {
  // Enforce anonymous cap of 3 shared wheels via account policy (reactive, DRY)
  try {
    await ensurePolicyFresh();
  } catch (e) {
    console.warn('Error ensuring policy before cap check:', e);
  }

  if (!isLoggedIn.value && !canCreateAnotherSharedWheel.value) {
    const ui = instance?.appContext.config.globalProperties.$ui || window.$ui;
    ui?.dialog.confirm({
      title: t('sharedialog.Shareable link'),
      message:
        'Guest limit reached: You can create up to 3 shared wheels without an account. Create a free account to create more and sync across devices.',
      cancelText: t('common.Cancel'),
      confirmText: t('common.Continue'),
      type: 'is-info',
      hasIcon: true,
      onConfirm: () => emit('open-account-dialog')
    });
    return;
  }

  setState('CreatingLink');
  try {
    emit('start-wait-animation');
    wheelStore.setWheelTitle(wheelTitle.value);
    const path = await userStore.shareWheel({
      wheelConfig: wheelConfig.value,
      copyableWheel: copyableWheel.value
    });
    const relativePath = Locales.getRelativeUrl(window.location.host, locale.value) + path;
    sharableLink.value = window.location.origin + relativePath;
    emit('stop-wait-animation');
    enter_ShowingLink();
  } catch (ex) {
    console.error('Error creating shared wheel:', ex);
    Util.trackException(ex);
    emit('stop-wait-animation');
    let errorMessage = 'Failed to create shared wheel. Please try again.';
    if (ex && typeof ex === 'object') {
      if (ex.message) {
        if (ex.message.includes('Server responded with 451:')) {
          try {
            const jsonMatch = ex.message.match(/\{[^}]*"error":\s*"([^"]+)"/);
            if (jsonMatch && jsonMatch[1]) errorMessage = jsonMatch[1];
            else errorMessage = 'This wheel contains content that isn\'t allowed for sharing. Please check your entries and try again.';
          } catch {
            errorMessage = 'This wheel contains content that isn\'t allowed for sharing. Please check your entries and try again.';
          }
        } else {
          errorMessage = ex.message;
        }
      } else if (ex.error) {
        errorMessage = ex.error;
      } else if (ex.toString && ex.toString() !== '[object Object]') {
        errorMessage = ex.toString();
      }
    } else if (typeof ex === 'string') {
      errorMessage = ex;
    }
    const ui = instance?.appContext.config.globalProperties.$ui || window.$ui;
    ui?.dialog.alert({ title: 'Unable to Share Wheel', message: errorMessage, type: 'is-danger', hasIcon: true });
  }
};

const enter_confirmingDelete = (path) => {
  fsm.value = 'ConfirmingDelete';
  const ui = instance?.appContext.config.globalProperties.$ui || window.$ui;
  ui?.dialog.confirm({
    title: t('opendialog.Delete wheel'),
    message: t('opendialog.Are you sure', { wheelTitle: Util.escapeHtml(path) }),
    cancelText: t('common.Cancel'),
    confirmText: t('common.Delete'),
    type: 'is-danger',
    hasIcon: true,
    onConfirm: () => enter_deletingWheel(path),
    onCancel: () => setState('ShowingSharedWheels')
  });
};

const enter_deletingWheel = async (path) => {
  fsm.value = 'DeletingWheel';
  try {
    emit('start-wait-animation');
    await userStore.deleteSharedWheel(path);
    emit('stop-wait-animation');
    setState('ShowingSharedWheels');
  } catch (ex) {
    console.error('Error deleting wheel:', ex);
    Util.trackException(ex);
    emit('stop-wait-animation');
    alert('Failed to delete wheel. Please try again.');
    setState('ShowingSharedWheels');
  }
};

const enter_authError = (exception) => {
  fsm.value = 'authError';
  userStore.logOut();
  enter_Inactive();
};

const enter_ShowingLink = () => setState('ShowingLink');
const enter_Inactive = () => setState('Inactive');

const copyLink = () => {
  Util.trackEvent('Wheel', 'CopySharableLink', '');
  const textBox = document.querySelector('#sharableLinkText');
  textBox?.select?.();
  document.execCommand('copy');
  const message = t('sharedialog.Link copied to the clipboard');
  emit('show-snackbar-message', message);
};

const setState = (newState) => { fsm.value = newState; };
const getWheelLink = (path) => {
  const relativePath = Locales.getRelativeUrl(window.location.host, locale.value) + path;
  return window.location.origin + relativePath;
};
const copyToClipboard = async (link) => {
  await navigator.clipboard.writeText(link);
  const message = t('sharedialog.Link copied to the clipboard');
  emit('show-snackbar-message', message);
};
const formatTimeAgo = (timestamp) => timeago(firestoremilliseconds(timestamp));
const formatLocaleString = (value) => localestring(value);
const handleResize = () => { windowWidth.value = window.innerWidth; };

const onStorage = (event) => {
  try {
    if (event && event.key === 'wheelManagementTokens') {
      // Update the shared wheels list when tokens change in localStorage (cross-tab or in-page)
      userStore.loadSharedWheels();
    }
  } catch (e) {
    console.error('Error handling storage event:', e);
  }
};

defineExpose({ show, enter_LoadingSharedWheels });
</script>
